<template>
  <div ref="anchorTop" class="article-index">
    <transition name="fade-slide__y-100">
      <div v-if="hasMounted" class="article-search mb-6">
        <div class="relative">
          <button class="absolute top-0 p-3 text-gray-400 focus:outline-none article-search-button_search"
                  :disabled="!hasSearchQuery"
                  @click.prevent="search">
            <icon-base viewBox="0 0 14 14">
              <icon-search/>
            </icon-base>
          </button>
          <input type="text"
                 class="w-full px-12 py-2 bg-transparent border border-gray-800 rounded-3xl focus:outline-none"
                 name="search"
                 v-model.trim="searchQuery"
                 placeholder="Поиск">
          <button class="absolute top-0 p-3 text-gray-400 focus:outline-none article-search-button_reset"
                  :class="{hidden : !hasSearchQuery}"
                  :disabled="!hasSearchQuery"
                  @click.prevent="resetSearch">
            <icon-base viewBox="0 0 20 20" width="14" height="14">
              <icon-cross/>
            </icon-base>
          </button>
        </div>
      </div>
    </transition>
    <ul v-if="hasTagList" class="flex flex-wrap gap-2.5 mb-8">
      <li v-for="tag in tagList"
          :key="tag.id"
          class="article-tag bg-purple-150 cursor-pointer rounded-lg px-2 py-1.5 leading-none w-min"
          :class="{'bg-secondary-lightest' : initialTag !== tag.id}"
          @click="toggleTag"
          :data-id="tag.id">
        #{{ tag.name }}
      </li>
    </ul>
    <div class="articles-grid" v-if="hasArticleList">
      <article-card
        v-for="article in articleList"
        :key="article.id"
        :article="article"
        :selected-tags="selectedTags"/>
    </div>
    <div class="my-6 lg:h-9 flex justify-center">
      <pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @update:current-page="updateCurrentPage"/>
    </div>
  </div>
</template>

<script>
import fetchClient from "../../../../plugins/fetchClient";
import Pagination from "../../ui/Pagination.vue";
import ArticleCard from "../ArticleCard.vue";
import IconBase from "../../icons/IconBase.vue";
import IconSearch from "../../icons/IconSearch.vue";
import IconCross from "../../icons/IconCross.vue";

export default {
  components: {
    Pagination,
    ArticleCard,
    IconBase,
    IconSearch,
    IconCross
  },
  props: {
    initialTag: {
      type: [Number, String],
      default: ''
    }
  },
  data() {
    return {
      totalPages: 1,
      currentPage: 1,
      perPage: 12,
      hasMounted: false,
      searchQuery: '',
      searchTimerId: null,
      articleList: [],
      tagList: [],
      selectedTags: []
    }
  },
  watch: {
    currentPage() {
      if (this.hasSearchQuery) {
        this.search()
      } else {
        this.getArticles()
      }
    },
    searchQuery() {
      clearTimeout(this.searchTimerId); // Отменить предыдущий таймер
      this.searchTimerId = setTimeout(() => {
        if (this.currentPage === 1) {
          this.search()
        } else {
          this.currentPage = 1
        }
      }, 500);
    },
    selectedTags: {
      handler() {
        this.currentPage = 1
        this.search()
      },
      deep: true
    }
  },
  methods: {
    async getArticles() {
      try {
        const data = await fetchClient("/articles", {queryParams: {page: this.currentPage, per_page: this.perPage}})
        this.articleList = data["articles"]
        this.totalPages = data["total_pages"]
      } catch(error) {
        console.error(error)
      }
    },
    async getInitialData() {
      try {
        const data = await fetchClient("/articles/tags")
        this.tagList = data
        // На случай передачи некорректного изначально выбранного тега
        if (this.tagIds.includes(this.initialTag)) {
          this.selectedTags.push(this.initialTag)
        } else {
          this.getArticles()
        }
      } catch(error) {
        console.error(error)
      }
    },
    updateCurrentPage(page) {
      this.currentPage = page
      this.scrollToTop()
    },
    scrollToTop() {
      const element = this.$refs.anchorTop
      const headerHeight = document.getElementsByTagName('header')[0].offsetHeight
      const offsetTop = element.offsetTop - headerHeight
      const currentPosition = document.documentElement.scrollTop || document.body.scrollTop
      if (currentPosition > offsetTop) {
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    },
    resetSearch() {
      this.searchQuery = ''
    },
    async search() {
      const queryParams = {
        search: this.searchQuery,
        page: this.currentPage,
        per_page: this.perPage,
        tags: this.selectedTags
      }
      const data = await fetchClient("/articles", {queryParams: queryParams})
      this.articleList = data["articles"]
      this.totalPages = data["total_pages"]
    },
    toggleTag(event) {
      event.target.classList.toggle("bg-secondary-lightest")
      const id = parseInt(event.target.getAttribute("data-id"))
      this.selectedTags.includes(id) ? this.selectedTags.splice(this.selectedTags.indexOf(id)) : this.selectedTags.push(id)
    }
  },
  computed: {
    hasArticleList() {
      return this.articleList && this.articleList.length > 0
    },
    hasSearchQuery() {
      return Boolean(this.searchQuery)
    },
    hasTagList() {
      return this.tagList && this.tagList.length > 0
    },
    hasInitialTag() {
      return this.initialTag && this.initialTag !== ''
    },
    tagIds() {
      return this.tagList.map(tag => tag.id)
    }
  },
  mounted() {
    this.hasMounted = true
    this.getInitialData()
  }
}
</script>

<style scoped>
.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(295px, 1fr));
  grid-auto-rows: 1fr;
  gap: 20px;
}
</style>